import { ComponentType, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import PrivateRoute from "src/components/PrivateRoute";
import { useAppSelector } from "src/app/hooks";
import { selectAuth } from "src/features/authSlice";
import { runtimeConfig } from "src/services/runtimeConfig";
import styles from "./SysAdminLayout.module.css";

interface SysAdminLayoutProps {
  Component: ComponentType;
  PageTitle?: string;
  breadcrumbs?: { label: string; href: string }[];
}

const useCurrentPath = () => {
  const [currentPath, setCurrentPath] = useState(
    window.location.pathname + window.location.search,
  );

  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname + window.location.search);
    };

    window.addEventListener("popstate", handleLocationChange);
    return () => window.removeEventListener("popstate", handleLocationChange);
  }, []);

  return currentPath;
};

const SysAdminLayout: React.FC<SysAdminLayoutProps> = ({
  Component,
  PageTitle = "",
  breadcrumbs,
}) => {
  const currentPath = useCurrentPath();
  const { isSysadmin, hasSalesReportAccess } = useAppSelector(selectAuth);

  // Sales-only users (no sysadmin claim) who reach a shared sysadmin page
  // should see the Sales Dashboard breadcrumb, not "Sysadmin :".
  const showSalesBreadcrumb = !isSysadmin && hasSalesReportAccess;

  return (
    <PrivateRoute>
      <Helmet>
        <title>DNCScrub: {PageTitle}</title>
        <link
          href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.5/font/bootstrap-icons.css"
          rel="stylesheet"
        />
      </Helmet>
      <div>
        <div className={styles.headerRow}>
          <h2 className={styles.heading}>
            {showSalesBreadcrumb ? (
              <a href="/sales">Sales Dashboard</a>
            ) : (
              <a href={`${runtimeConfig.classicUrl}/app/sysadmin/index.adp`}>
                Sysadmin
              </a>
            )}{" "}
            {breadcrumbs?.map((bc, i) => (
              <span key={i}>
                : <a href={bc.href}>{bc.label}</a>{" "}
              </span>
            ))}
            : <a href={currentPath}>{PageTitle}</a>
          </h2>
          <div className={styles.logoutContainer}>
            <span className={styles.body2B}>
              <a
                href={`${runtimeConfig.classicUrl}/app/scripts/logout`}
                target="_top"
                className={styles.link3}
              >
                Log&nbsp;Out&nbsp;
                <img
                  src={`${runtimeConfig.classicUrl}/images/blue/logout.gif`}
                  width={45}
                  height={24}
                  style={{
                    border: 0,
                    verticalAlign: "middle",
                    marginLeft: "4px",
                  }}
                  alt="Logout"
                />
              </a>
            </span>
          </div>
        </div>
        <div className={styles.container}>
          <Component />
        </div>
      </div>
    </PrivateRoute>
  );
};

export default SysAdminLayout;
